import React, { useState, useEffect } from 'react';
import { Container, Button, Modal, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Card from './Card';
import Icon from '../../UI/Icon';
import { getImagesByGermanWords, getWordDataByGermanWords, getAllWords } from '../../../services/gameService';
import { getProblemSounds } from '../../../services/pronunciationTestService';
import Confetti from 'react-confetti';
import sprechheldenHeldRight from '../../../assets/sprechheldenHeld_right.png';
import sprechheldenHeldFlying from '../../../assets/sprechheldenHeld_flying.png';
import background1 from '../../../assets/background_1.png';
import gameWon from '../../../assets/gameWon.png';
import helpLogo from '../../../assets/logo_logo_sh.png';
import { auth } from '../../../firebase';
import { LuGamepad2 } from "react-icons/lu";
import { IoVolumeMuteOutline } from "react-icons/io5";
import FloatingLautbilderButton from '../../FloatingLautbilderButton';
import { isPhone as checkIsPhone, isTabletPortrait, setupDeviceDetection } from '../../../utils/deviceDetection';

function MemoryGame({ difficulty = 'easy', onBackToGames }) {
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [selectedLauts, setSelectedLauts] = useState([]);
  const [availableLauts, setAvailableLauts] = useState([]);
  const [showLautModal, setShowLautModal] = useState(true);
  const [gameStarted, setGameStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [autoFlippingCards, setAutoFlippingCards] = useState([]);
  const [glowingCards, setGlowingCards] = useState([]);
  const [gameMode, setGameMode] = useState('single'); // 'single' or 'two-player'
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [playerScores, setPlayerScores] = useState({ player1: 0, player2: 0 });
  const [showWordPrompt, setShowWordPrompt] = useState(false);
  const [matchedWord, setMatchedWord] = useState('');
  const [isGameCompleteAfterPrompt, setIsGameCompleteAfterPrompt] = useState(false);
  const [databaseWords, setDatabaseWords] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isPhone, setIsPhone] = useState(false);
  const [isTabletPortraitMode, setIsTabletPortraitMode] = useState(false);
  const [isLocked, setIsLocked] = useState(false); // Prevent clicks while processing cards
  const [isZoomed, setIsZoomed] = useState(false); // Track if browser is zoomed in
  const [preloadProgress, setPreloadProgress] = useState(0); // Image preload progress (0-100)
  const [isPreloading, setIsPreloading] = useState(false); // Whether images are being preloaded

  // Fetch words from database on component mount and extract lauts
  useEffect(() => {
    const fetchWordsAndUserData = async () => {
      try {
        const words = await getAllWords();
        setDatabaseWords(words);
        
        // Extract unique lauts from words (laut is an array field)
        const allLautsBeforeFilter = words
          .filter(word => word.laut && Array.isArray(word.laut)) // Only words with laut array
          .flatMap(word => word.laut); // Flatten all laut arrays into one array
        
        const lauts = allLautsBeforeFilter
          .map(laut => laut && typeof laut === 'string' ? laut.trim() : laut) // Trim whitespace
          .filter(laut => laut && typeof laut === 'string' && laut !== '') // Remove empty/falsy values
          .filter((laut, index, array) => array.indexOf(laut) === index) // Remove duplicates
          .sort(); // Sort alphabetically
        
        setAvailableLauts(lauts);

        // Fetch user's severe problem sounds and pre-select them
        const user = auth.currentUser;
        if (user) {
          // Use getProblemSounds service to get sounds from pronunciation tests collection
          const problemSoundsData = await getProblemSounds(user.uid);
          const severeSounds = problemSoundsData.severeProblemSounds;

          if (severeSounds && Array.isArray(severeSounds) && severeSounds.length > 0) {
            // Pre-select severe problem sounds that are available in the game (case-insensitive)
            const soundsToPreselect = severeSounds.filter(sound =>
              lauts.some(laut => laut.toLowerCase() === sound.toLowerCase())
            ).map(sound => {
              // Return the actual laut from available lauts (with correct casing)
              return lauts.find(laut => laut.toLowerCase() === sound.toLowerCase());
            }).filter(Boolean);

            // Limit to max 4 sounds for Memory game
            const limitedSounds = soundsToPreselect.slice(0, 4);
            setSelectedLauts(limitedSounds);
          }
        }
      } catch (error) {
        console.error('Error fetching words from database:', error);
      }
    };

    fetchWordsAndUserData();
  }, []);

  // Phone and tablet portrait detection on component mount
  useEffect(() => {
    const cleanup = setupDeviceDetection(() => {
      setIsPhone(checkIsPhone());
      setIsTabletPortraitMode(isTabletPortrait());
    });
    return cleanup;
  }, []);

  // Detect browser zoom level - hide hero images when zoomed in (desktop only)
  useEffect(() => {
    // Skip zoom detection on mobile/touch devices - outerWidth/innerWidth is unreliable on iOS
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    const detectZoom = () => {
      const zoomLevel = window.outerWidth / window.innerWidth;
      setIsZoomed(zoomLevel > 1.1);
    };

    detectZoom();
    window.addEventListener('resize', detectZoom);
    return () => window.removeEventListener('resize', detectZoom);
  }, []);

  // Prevent iOS Safari zoom gestures during gameplay
  useEffect(() => {
    if (!gameStarted) return;

    // Prevent double-tap zoom and pinch zoom on the game area
    const preventZoom = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const preventGestureZoom = (e) => {
      e.preventDefault();
    };

    document.addEventListener('touchstart', preventZoom, { passive: false });
    document.addEventListener('gesturestart', preventGestureZoom);
    document.addEventListener('gesturechange', preventGestureZoom);
    document.addEventListener('gestureend', preventGestureZoom);

    // Set viewport to prevent zoom on iOS
    const viewport = document.querySelector('meta[name="viewport"]');
    const originalContent = viewport?.getAttribute('content');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    }

    return () => {
      document.removeEventListener('touchstart', preventZoom);
      document.removeEventListener('gesturestart', preventGestureZoom);
      document.removeEventListener('gesturechange', preventGestureZoom);
      document.removeEventListener('gestureend', preventGestureZoom);

      // Restore original viewport when leaving the game
      if (viewport && originalContent) {
        viewport.setAttribute('content', originalContent);
      }
    };
  }, [gameStarted]);

  // Handle Enter key press for word prompt modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (showWordPrompt && event.key === 'Enter') {
        handleWordPromptClose();
      }
    };

    if (showWordPrompt) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showWordPrompt]);

  const selectGameMode = (mode) => {
    setGameMode(mode);
  };

  const handleWordPromptClose = () => {
    setShowWordPrompt(false);
    // Check if game is complete after closing the prompt
    if (isGameCompleteAfterPrompt) {
      setGameComplete(true);
      setShowConfetti(true);
      setIsGameCompleteAfterPrompt(false);
      // Stop confetti after 5 seconds
      setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
    }
  };

  // Help function - shows one random pair of cards by making them glow
  const showHelp = () => {
    if (gameComplete || cards.length === 0) return;

    // Find all unmatched cards
    const unmatchedCards = cards.filter(card => !matchedCards.includes(card.id));
    
    // Group cards by word to find pairs
    const cardPairs = {};
    unmatchedCards.forEach(card => {
      if (!cardPairs[card.word]) {
        cardPairs[card.word] = [];
      }
      cardPairs[card.word].push(card.id);
    });

    // Get pairs that have exactly 2 cards (complete pairs)
    const completePairs = Object.values(cardPairs).filter(pair => pair.length === 2);
    
    // If we have no pairs, don't show anything
    const pairsToShow = Math.min(1, completePairs.length);
    if (pairsToShow === 0) return; // No pairs to show

    // Randomly select one pair to highlight
    const shuffledPairs = completePairs.sort(() => Math.random() - 0.5);
    const selectedPairs = shuffledPairs.slice(0, pairsToShow);
    
    // Get all card IDs from selected pair
    const cardsToGlow = selectedPairs.flat();
    
    // Set glowing cards
    setGlowingCards(cardsToGlow);
    
    // Remove glow after 3 seconds
    setTimeout(() => {
      setGlowingCards([]);
    }, 3000);
  };

  // Function to play audio
  const playAudio = (audioUrl) => {
    if (audioUrl && !isMuted) {
      try {
        const audio = new Audio(audioUrl);
        audio.play().catch(error => {
          console.error('Error playing audio:', error);
        });
      } catch (error) {
        console.error('Error creating audio object:', error);
      }
    }
  };

  // Handle laut selection with max 4 validation for Memory game  
  const handleLautSelection = (laut) => {
    setSelectedLauts(prev => {
      if (prev.includes(laut)) {
        // Remove laut if already selected
        return prev.filter(l => l !== laut);
      } else {
        // Add laut if not selected and under limit
        if (prev.length < 4) {
          return [...prev, laut];
        }
        return prev; // Don't add if already at max
      }
    });
  };

  const generateWords = () => {
    // Filter database words based on selected lauts
    const filteredWords = databaseWords.filter(wordData => {
      // Check if word exists and has lauts that match any of the selected lauts
      return wordData.word && 
             wordData.laut && 
             Array.isArray(wordData.laut) &&
             selectedLauts.some(selectedLaut => wordData.laut.includes(selectedLaut));
    });

    // Extract just the word strings for the game
    const words = filteredWords.map(wordData => wordData.word);

    // Adjust pair count based on difficulty
    const getPairCount = () => {
      switch(difficulty) {
        case 'easy':
          return 8; // 16 cards (8 pairs)
        case 'medium':
          return 12; // 24 cards (12 pairs)
        case 'hard':
          return 20; // 40 cards (20 pairs)
        default:
          return 8; // Default to easy
      }
    };

    // Mix words and take appropriate number of pairs based on difficulty
    const shuffled = words.sort(() => Math.random() - 0.5);
    const pairCount = getPairCount();
    
    return shuffled.slice(0, pairCount);
  };

  // Start game with selected lauts
  const startGameWithLauts = () => {
    if (selectedLauts.length === 0) {
      alert('Bitte wählen Sie mindestens einen Laut aus!');
      return;
    }
    
    const availableWords = getAvailableWordsCount();
    const minimumNeeded = getMinimumWordsNeeded();
    
    if (availableWords < minimumNeeded) {
      const difficultyText = difficulty === 'easy' ? 'Einfach' : 
                           difficulty === 'medium' ? 'Mittel' : 'Schwer';
      alert(`Nicht genügend Wörter für Schwierigkeitsgrad "${difficultyText}"!\n\nBenötigt: ${minimumNeeded} Wörter\nVerfügbar: ${availableWords} Wörter\n\nBitte wählen Sie zusätzliche Laute aus oder ändern Sie den Schwierigkeitsgrad.`);
      return;
    }
    
    setShowLautModal(false);
    setGameStarted(true);
  };

  // Reset laut selection
  const resetLautSelection = () => {
    setSelectedLauts([]);
  };

  // Function to count available words for selected lauts
  const getAvailableWordsCount = () => {
    if (selectedLauts.length === 0) return 0;
    
    const filteredWords = databaseWords.filter(wordData => {
      return wordData.word && 
             wordData.laut && 
             Array.isArray(wordData.laut) &&
             selectedLauts.some(selectedLaut => wordData.laut.includes(selectedLaut));
    });
    
    return filteredWords.length;
  };

  // Function to get minimum words needed for difficulty
  const getMinimumWordsNeeded = () => {
    switch(difficulty) {
      case 'easy':
        return 8; // 8 pairs = 16 cards
      case 'medium':
        return 12; // 12 pairs = 24 cards
      case 'hard':
        return 20; // 20 pairs = 40 cards
      default:
        return 8;
    }
  };

  // Check if we have enough words for the game
  const hasEnoughWords = () => {
    const availableWords = getAvailableWordsCount();
    const minimumNeeded = getMinimumWordsNeeded();
    return availableWords >= minimumNeeded;
  };

  const initializeGame = async () => {
    try {
      setLoading(true);
      setIsPreloading(false);
      setPreloadProgress(0);
      const words = generateWords();

      // Fetch images and word data (including audio) for all unique words
      const [imageMap, wordDataMap] = await Promise.all([
        getImagesByGermanWords(words),
        getWordDataByGermanWords(words)
      ]);

      const cardPairs = [...words, ...words];
      const shuffledCards = cardPairs
        .sort(() => Math.random() - 0.5)
        .map((word, index) => ({
          id: index,
          word,
          image: imageMap[word] || null,
          audio: wordDataMap[word]?.audio || null,
          isFlipped: false,
          isMatched: false
        }));

      // Preload all card images before showing the game
      const imageUrls = shuffledCards
        .map(card => {
          if (!card.image) return null;
          return card.image.imageUrl || card.image.url || card.image.downloadURL || card.image.src;
        })
        .filter(Boolean)
        // Deduplicate URLs since pairs share the same image
        .filter((url, index, arr) => arr.indexOf(url) === index);

      if (imageUrls.length > 0) {
        setIsPreloading(true);
        setLoading(false); // Switch from generic loading to preload progress
        let loaded = 0;
        const minDisplayTime = new Promise((resolve) => setTimeout(resolve, 3000));

        await Promise.all([
          minDisplayTime,
          ...imageUrls.map(
            (url) =>
              new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                  loaded++;
                  setPreloadProgress(Math.round((loaded / imageUrls.length) * 100));
                  resolve();
                };
                img.onerror = () => {
                  loaded++;
                  setPreloadProgress(Math.round((loaded / imageUrls.length) * 100));
                  resolve(); // Don't block on failed images
                };
                img.src = url;
              })
          )
        ]);
        setIsPreloading(false);
      }

      setCards(shuffledCards);
      setFlippedCards([]);
      setMatchedCards([]);
      setMoves(0);
      setGameComplete(false);
      setShowConfetti(false);
      setCurrentPlayer(1);
      setPlayerScores({ player1: 0, player2: 0 });
      setShowWordPrompt(false);
      setMatchedWord('');
      setIsGameCompleteAfterPrompt(false);
      setGlowingCards([]);
      setIsLocked(false); // Reset lock state
    } catch (error) {
      console.error('Error initializing game:', error);
    } finally {
      setLoading(false);
      setIsPreloading(false);
    }
  };

  useEffect(() => {
    if (gameStarted && selectedLauts.length > 0) {
      initializeGame();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStarted, selectedLauts]);

  const handleCardClick = (cardId) => {
    // Prevent clicks while processing cards
    if (isLocked) {
      return;
    }

    const card = cards.find(c => c.id === cardId);

    // If card is already flipped or matched, just play audio
    if (matchedCards.includes(cardId) || flippedCards.includes(cardId)) {
      playAudio(card.audio);
      return;
    }

    // If two cards are already flipped, don't allow more clicks
    if (flippedCards.length >= 2) {
      return;
    }

    // Play audio immediately when card is flipped
    playAudio(card.audio);

    // Use functional update to avoid race conditions with rapid clicks
    setFlippedCards(prevFlippedCards => {
      // Safety check - prevent adding more than 2 cards
      if (prevFlippedCards.length >= 2) {
        return prevFlippedCards;
      }

      // Prevent adding the same card twice
      if (prevFlippedCards.includes(cardId)) {
        return prevFlippedCards;
      }

      const newFlippedCards = [...prevFlippedCards, cardId];

      // If this is the second card, process the match/mismatch
      if (newFlippedCards.length === 2) {
        // Lock the game to prevent further clicks during processing
        setIsLocked(true);
        setMoves(m => m + 1);

        const [firstId, secondId] = newFlippedCards;
        const firstCard = cards.find(c => c.id === firstId);
        const secondCard = cards.find(c => c.id === secondId);

        if (firstCard.word === secondCard.word) {
          // Cards match
          const newMatchedCards = [...matchedCards, firstId, secondId];
          setMatchedCards(newMatchedCards);
          setIsLocked(false); // Unlock immediately for matches

          // Show word prompt
          setMatchedWord(firstCard.word);
          setShowWordPrompt(true);

          // Update player score in two-player mode
          if (gameMode === 'two-player') {
            const currentPlayerKey = currentPlayer === 1 ? 'player1' : 'player2';
            setPlayerScores(prev => ({
              ...prev,
              [currentPlayerKey]: prev[currentPlayerKey] + 1
            }));
          }

          // Check if game is complete (will be handled when user closes the prompt)
          if (newMatchedCards.length === cards.length) {
            setIsGameCompleteAfterPrompt(true);
          }

          // Return empty array to clear flipped cards for matches
          return [];
        } else {
          // Cards don't match - keep them flipped for a moment, then flip back
          setTimeout(() => {
            setFlippedCards([]);

            // Unlock after CSS flip-back transition completes (500ms)
            setTimeout(() => {
              setIsLocked(false);

              // Switch player in two-player mode
              if (gameMode === 'two-player') {
                setCurrentPlayer(prevPlayer => prevPlayer === 1 ? 2 : 1);
              }
            }, 500);
          }, 1000);

          // Return the new flipped cards for mismatches (they'll be cleared by setTimeout)
          return newFlippedCards;
        }
      }

      return newFlippedCards;
    });
  };

  const getGridColumns = () => {
    switch(difficulty) {
      case 'easy':
        return 4; // 4x4 grid for 16 cards
      case 'medium':
        return 6; // 6x4 grid for 24 cards
      case 'hard':
        return 8; // 8x5 grid for 40 cards
      default:
        return 4;
    }
  };
  
  const resetGame = () => {
    setGameStarted(false);
    setGameComplete(false);
    setShowConfetti(false);
    setCards([]);
    setFlippedCards([]);
    setMatchedCards([]);
    setMoves(0);
    setLoading(false);
    setIsPreloading(false);
    setPreloadProgress(0);
    setCurrentPlayer(1);
    setPlayerScores({ player1: 0, player2: 0 });
    setGameMode('single');
    setShowLautModal(true);
    setShowWordPrompt(false);
    setMatchedWord('');
    setIsGameCompleteAfterPrompt(false);
    setGlowingCards([]);
    setIsLocked(false); // Reset lock state
  };

  return (
    <div className="memory-game-main-content" style={{
      position: 'relative',
      minHeight: '100vh',
      width: '100%',
      margin: 0,
      padding: isPhone ? '30px 15px' : '20px',
      overflow: 'hidden'
    }}>
      {!isPhone && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          minHeight: '100vh',
          backgroundImage: `url(${background1})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          opacity: 0.2,
          zIndex: 0
        }} />
      )}
    <Container style={{ position: 'relative', zIndex: 1, maxWidth: '100%' }}>
      {/* Mobile devices now supported with auto-selected easy difficulty */}
      <>
      <style>
        {`
          /* Memory game specific styles - scoped to component */
          .memory-game-main-content {
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden;
          }
          
          /* Override parent container paddings for MemoryGame - desktop keeps normal padding */
          .memory-game-main-content .main-content {
            max-width: 100% !important;
          }
          
          .memory-game-main-content .content-area {
            max-width: 100% !important;
          }
          
          /* Remove padding only on mobile for maximum card size */
          @media (max-width: 768px) {
            .memory-game-main-content .main-content {
              padding-left: 0 !important;
              padding-right: 0 !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            
            .memory-game-main-content .content-area {
              padding-left: 0 !important;
              padding-right: 0 !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
          }
          
          /* Mobile-specific card styles */
          @media (max-width: 768px) {
            .memory-game-grid {
              grid-template-columns: repeat(4, 1fr) !important; /* Override CSS to maintain 4 columns for easy mode */
              gap: 8px !important;
              align-items: stretch;
              align-content: start;
              grid-auto-rows: 1fr;
            }
            
            .memory-card {
              min-height: 80px !important; /* Fixed minimum height instead of viewport calculation */
              height: 80px !important; /* Fixed height for consistency */
              font-size: 1.2rem !important;
            }
            
            .card-container {
              aspect-ratio: 1 / 1 !important; /* Square cards on mobile */
              height: 100%;
              margin: 2px 1px !important;
            }
            
            .card-image {
              max-width: 85% !important;
              max-height: 85% !important;
            }
            
            .card-back-logo {
              max-width: 80% !important;
              max-height: 80% !important;
            }
          }
          
          /* Even smaller screens */
          @media (max-width: 480px) {
            .memory-card {
              min-height: 70px !important; /* Fixed minimum height for smaller screens */
              height: 70px !important; /* Fixed height for consistency */
              font-size: 1rem !important;
            }
          }
          
          /* Override Bootstrap container max-width and margins */
          .memory-game-main-content .container {
            max-width: 100% !important;
            padding-left: 15px !important;
            padding-right: 15px !important;
          }
          
          /* Remove container padding on mobile */
          @media (max-width: 768px) {
            .memory-game-main-content .container {
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            
            /* Remove all padding from memory-game-main-content wrapper */
            .memory-game-main-content {
              padding-left: 0 !important;
              padding-right: 0 !important;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            
            /* Global main-content padding removal on mobile for MemoryGame */
            .main-content {
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
          }
          
          /* Memory game grid styles */
          .memory-game-grid {
            display: grid !important;
            gap: 15px;
            justify-content: center;
            align-items: stretch;
            align-content: start;
            width: 100%;
            grid-auto-rows: 1fr;
            touch-action: manipulation;
          }
          
          /* Desktop card styles */
          @media (min-width: 769px) {
            .memory-card {
              min-height: 120px !important;
              height: 120px !important;
              font-size: 1.2rem !important;
            }
          }
          
          @keyframes progressPulse {
            0% {
              opacity: 0.6;
            }
            50% {
              opacity: 1;
            }
            100% {
              opacity: 0.6;
            }
          }

          @keyframes memoryFadeIn {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }
          
          @keyframes bounceCheckmark {
            0% {
              transform: scale(0) rotate(0deg);
              opacity: 0;
            }
            50% {
              transform: scale(1.3) rotate(180deg);
            }
            100% {
              transform: scale(1) rotate(360deg);
              opacity: 1;
            }
          }
          
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7);
            }
            70% {
              box-shadow: 0 0 0 15px rgba(255, 107, 107, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 107, 107, 0);
            }
          }
          
          @keyframes pulseCyan {
            0% {
              box-shadow: 0 0 0 0 rgba(78, 205, 196, 0.7);
            }
            70% {
              box-shadow: 0 0 0 15px rgba(78, 205, 196, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(78, 205, 196, 0);
            }
          }
          
          @keyframes pulseGray {
            0% {
              box-shadow: 0 0 0 0 rgba(153, 153, 153, 0.7);
            }
            70% {
              box-shadow: 0 0 0 15px rgba(153, 153, 153, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(153, 153, 153, 0);
            }
          }
          
          @keyframes float {
            0% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-15px);
            }
            100% {
              transform: translateY(0px);
            }
          }
          
          .memory-players-mobile {
            display: none;
          }
          
          .memory-players-mobile-sides {
            display: none;
          }
          
          @media (max-width: 1200px) {
            .memory-players-desktop {
              display: none !important;
            }
            
            .memory-players-mobile {
              display: flex !important;
              justify-content: space-between;
              width: 100%;
              margin-bottom: 20px;
              padding: 0 20px;
            }
            
            .memory-players-mobile-sides {
              display: flex !important;
            }
            
            .memory-players-desktop-sides {
              display: none !important;
            }
            
            .memory-game-container {
              padding: 0 !important;
            }
          }
          
          /* Force mobile player indicators to show for hard mode on all screen sizes */
          .memory-players-mobile-sides.hard-mode {
            display: flex !important;
          }
          
          .sprechhelden-hero-images {
            display: block;
          }
          
          @media (max-width: 768px) {
            .memory-game-grid {
              gap: 8px !important;
            }
          }
          
          /* Hide Sprechhelden hero images on mobile and tablets */
          @media (max-width: 1180px) {
            .sprechhelden-hero-images {
              display: none !important;
            }
          }

          /* iPad Portrait - Optimize grid and card sizing */
          @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
            .memory-game-grid {
              gap: 12px !important;
            }

            .memory-card {
              min-height: 100px !important;
              height: 100px !important;
              font-size: 1.1rem !important;
            }

            .card-image {
              max-width: 90% !important;
              max-height: 90% !important;
            }

            .card-back-logo {
              max-width: 85% !important;
              max-height: 85% !important;
            }

            .speech-bubble-text {
              left: 280px !important;
              min-width: 240px !important;
              font-size: 16px !important;
            }

            .memory-game-main-content .container {
              padding-left: 10px !important;
              padding-right: 10px !important;
            }
          }
        `}
      </style>
      <div style={{
        backgroundColor: 'white',
        padding: isPhone ? '10px 15px' : '20px',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        margin: isPhone ? '0 auto 15px' : '0 auto 30px',
        width: 'fit-content'
      }}>
        <div className="text-center">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <h2 style={{ 
            margin: 0,
            color: 'inherit',
            fontSize: isPhone ? '1.5rem' : '2rem'
          }}>
            Zwillingspaare finden
          </h2>
          </div>
        </div>
      </div>


      {gameStarted ? (
        <>
          {/* Player scores at top for Schwer mode and mobile */}
          {gameMode === 'two-player' && !loading && (difficulty === 'hard' || window.innerWidth <= 1200) && (
            <div className={`memory-players-mobile-sides ${difficulty === 'hard' ? 'hard-mode' : ''}`} style={{
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              marginBottom: '20px',
              gap: '40px'
            }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: currentPlayer === 1 ? '#FF6B6B' : '#999'
                    }}>
                      Spieler 1
                    </div>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: currentPlayer === 1 ? '#FF6B6B' : '#999',
                      border: '2px solid #dee2e6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: 'white',
                      boxShadow: currentPlayer === 1 
                        ? '0 4px 12px rgba(255, 107, 107, 0.4)' 
                        : '0 2px 6px rgba(0, 0, 0, 0.2)',
                      animation: currentPlayer === 1 ? 'pulse 1s infinite' : 'none'
                    }}>
                      {playerScores.player1}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: currentPlayer === 2 ? '#4ECDC4' : '#999'
                    }}>
                      Spieler 2
                    </div>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: currentPlayer === 2 ? '#4ECDC4' : '#999',
                      border: '2px solid #dee2e6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: 'white',
                      boxShadow: currentPlayer === 2 
                        ? '0 4px 12px rgba(78, 205, 196, 0.4)' 
                        : '0 2px 6px rgba(0, 0, 0, 0.2)',
                      animation: currentPlayer === 2 ? 'pulseCyan 1s infinite' : 'none'
                    }}>
                      {playerScores.player2}
                    </div>
                  </div>
                </div>
              )}

          {(loading || isPreloading) && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: '20px'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                padding: isPhone ? '30px 25px' : '40px 50px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                maxWidth: '400px',
                width: isPhone ? '90%' : '100%'
              }}>
                <img
                  src={helpLogo}
                  alt="Loading"
                  style={{
                    width: '80px',
                    height: 'auto',
                    marginBottom: '20px',
                    animation: 'float 2s ease-in-out infinite'
                  }}
                />
                <p style={{
                  fontSize: isPhone ? '16px' : '18px',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '20px',
                  fontFamily: '"Poppins", sans-serif'
                }}>
                  {isPreloading ? 'Bilder werden geladen...' : 'Spiel wird vorbereitet...'}
                </p>
                {/* Progress bar */}
                <div style={{
                  width: '100%',
                  height: '12px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <div style={{
                    width: isPreloading ? `${preloadProgress}%` : '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, #4ECDC4, #44b8b0)',
                    borderRadius: '6px',
                    transition: 'width 0.3s ease',
                    animation: !isPreloading ? 'progressPulse 1.5s ease-in-out infinite' : 'none'
                  }} />
                </div>
                {isPreloading && (
                  <p style={{
                    fontSize: '14px',
                    color: '#888',
                    marginTop: '10px',
                    marginBottom: 0
                  }}>
                    {preloadProgress}%
                  </p>
                )}
              </div>
            </div>
          )}

          {!loading && !isPreloading && (
            <>
              <div className="memory-game-container" style={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                minHeight: '500px',
                width: '100%',
                margin: '0 auto'
              }}>
                {/* Desktop side player indicators for easy and medium difficulties only */}
                {gameMode === 'two-player' && difficulty !== 'hard' && (
                  <div className="memory-players-desktop-sides">
                    {/* Player 1 - Left side */}
                    <div style={{
                      position: 'absolute',
                      left: '60px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '10px',
                      zIndex: 10
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: currentPlayer === 1 ? '#FF6B6B' : '#999',
                        transition: 'color 0.3s ease',
                        textAlign: 'center'
                      }}>
                        Spieler 1
                      </div>
                      <div style={{
                        width: '70px',
                        height: '70px',
                        borderRadius: '50%',
                        backgroundColor: currentPlayer === 1 ? '#FF6B6B' : '#999',
                        border: '2px solid #dee2e6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: 'white',
                        boxShadow: currentPlayer === 1 
                          ? '0 6px 20px rgba(255, 107, 107, 0.4)' 
                          : '0 3px 8px rgba(0, 0, 0, 0.2)',
                        transition: 'all 0.3s ease',
                        animation: currentPlayer === 1 ? 'pulse 1s infinite' : 'none'
                      }}>
                        {playerScores.player1}
                      </div>
                    </div>
                    
                    {/* Player 2 - Right side */}
                    <div style={{
                      position: 'absolute',
                      right: '60px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '10px',
                      zIndex: 10
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: currentPlayer === 2 ? '#4ECDC4' : '#999',
                        transition: 'color 0.3s ease',
                        textAlign: 'center'
                      }}>
                        Spieler 2
                      </div>
                      <div style={{
                        width: '70px',
                        height: '70px',
                        borderRadius: '50%',
                        backgroundColor: currentPlayer === 2 ? '#4ECDC4' : '#999',
                        border: '2px solid #dee2e6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: 'white',
                        boxShadow: currentPlayer === 2 
                          ? '0 6px 20px rgba(78, 205, 196, 0.4)' 
                          : '0 3px 8px rgba(0, 0, 0, 0.2)',
                        transition: 'all 0.3s ease',
                        animation: currentPlayer === 2 ? 'pulseCyan 1s infinite' : 'none'
                      }}>
                        {playerScores.player2}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Sprechhelden Held Flying Left Image - only show during gameplay, not in hard mode, and not when zoomed */}
                {!gameComplete && difficulty !== 'hard' && !isZoomed && (
                  <div
                    className="sprechhelden-hero-images"
                    style={{
                    position: 'absolute',
                    left: '-10px',
                    top: '80%',
                    transform: 'translateY(-50%)',
                    zIndex: 5,
                    pointerEvents: 'none'
                  }}>
                    <img
                      src={sprechheldenHeldFlying}
                      alt="Sprechhelden Held Flying"
                      style={{
                        width: '220px',
                        height: 'auto',
                        opacity: 1
                      }}
                    />
                  </div>
                )}

                {/* Sprechhelden Held Right Image - only show during gameplay, not in hard mode, and not when zoomed */}
                {!gameComplete && difficulty !== 'hard' && !isZoomed && (
                  <div
                    className="sprechhelden-hero-images"
                    style={{
                    position: 'absolute',
                    right: '-20px',
                    top: '10%',
                    transform: 'translateY(-50%)',
                    zIndex: 5,
                    pointerEvents: 'none'
                  }}>
                    <img
                      src={sprechheldenHeldRight}
                      alt="Sprechhelden Held"
                      style={{
                        width: '220px',
                        height: 'auto',
                        opacity: 1
                      }}
                    />
                  </div>
                )}
                
                <div style={{ 
                  position: 'relative',
                  width: isPhone 
                    ? '100vw' // Use full viewport width on mobile
                    : (difficulty === 'easy' ? '600px' : 
                       difficulty === 'medium' ? '900px' : '1200px'),
                  maxWidth: '100%',
                  margin: '0 auto'
                }}>
                  {gameComplete ? (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      minHeight: isPhone ? '300px' : '400px',
                      position: 'relative'
                    }}>
                      {/* GameWon character with relative positioning */}
                      <div style={{ 
                        position: 'relative', 
                        display: 'inline-block',
                        marginLeft: isPhone ? '-30px' : '0'
                      }}>
                        <img 
                          src={gameWon} 
                          alt="Game Won Character" 
                          style={{
                            width: isPhone ? '200px' : '300px',
                            height: 'auto',
                            zIndex: 1,
                            marginTop: isPhone ? '50px' : '100px',
                            animation: 'float 3s ease-in-out infinite'
                          }}
                        />
                        
                        {/* Speech bubble text positioned relative to character */}
                        <div
                          className="speech-bubble-text"
                          style={{
                            position: 'absolute',
                            top: isPhone ? '-30px' : isTabletPortraitMode ? '-30px' : '30px',
                            left: isPhone ? '180px' : isTabletPortraitMode ? '430px' : '350px',
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            backgroundColor: '#ffffff',
                            padding: isPhone ? '8px 12px' : '12px 20px',
                            borderRadius: isPhone ? '15px' : '20px',
                            border: '2px solid #333333',
                            fontSize: isPhone ? '14px' : '18px',
                            color: '#333',
                            textAlign: 'center',
                            minWidth: isPhone ? '200px' : '280px',
                            fontFamily: '"Poppins", sans-serif',
                            fontWeight: '600'
                          }}
                        >
                          <span>Super! Du hast alle Wörter mit <strong>{selectedLauts.map(laut => `"${laut.toUpperCase()}"`).join(', ')}</strong> toll gesagt!</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="memory-game-grid"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)`,
                        gap: isPhone ? '8px' : '15px', // Add vertical spacing on mobile
                        maxWidth: 'none',
                        margin: '0 auto',
                        padding: isPhone ? '20px' : '20px', // Add padding for white background
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        borderRadius: '15px'
                      }}
                    >
                      {cards.map(card => {
                        const isFlipped = flippedCards.includes(card.id) || matchedCards.includes(card.id);
                        const canClick = matchedCards.includes(card.id) || (!card.isMatched && !isFlipped && flippedCards.length < 2 && !autoFlippingCards.includes(card.id));
                        
                        return (
                          <Card
                            key={card.id}
                            card={card}
                            isFlipped={isFlipped}
                            isAutoFlipping={autoFlippingCards.includes(card.id)}
                            isGlowing={glowingCards.includes(card.id)}
                            canClick={canClick}
                            onClick={() => handleCardClick(card.id)}
                            isMatched={matchedCards.includes(card.id)}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={{
                backgroundColor: 'white',
                padding: isPhone ? '8px 15px' : '10px 20px',
                borderRadius: '10px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                margin: isPhone ? '20px auto 0' : '40px auto 0',
                width: 'fit-content'
              }}>
                {isPhone ? (
                  // Mobile layout: Laut indicator on top, buttons below
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                    {/* Ausgewählte Laute indicator - centered with appropriate width on mobile */}
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="lauts-tooltip">Ausgewählte Laute</Tooltip>}
                    >
                      <div
                        style={{
                          width: 'fit-content',
                          minWidth: '100px',
                          maxWidth: '200px',
                          minHeight: '48px',
                          borderRadius: '30px',
                          backgroundColor: '#f8f9fa',
                          border: '2px solid #dee2e6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'default',
                          boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                          padding: '0 20px',
                          textAlign: 'center',
                          margin: '0 auto'
                        }}
                      >
                        {selectedLauts.join(', ')}
                      </div>
                    </OverlayTrigger>
                    
                    {/* Buttons row */}
                    <div className="d-flex justify-content-center gap-3 flex-wrap">
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="help-tooltip">Hilfe</Tooltip>}
                      >
                        <Button
                          onClick={showHelp}
                          variant="info"
                          size="lg"
                          style={{
                            minWidth: '48px',
                            minHeight: '48px',
                            borderRadius: '50%',
                            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                            padding: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          disabled={gameComplete || cards.length === 0}
                        >
                          <img 
                            src={helpLogo} 
                            alt="Hilfe" 
                            style={{ 
                              width: '28px', 
                              height: '28px',
                              objectFit: 'contain'
                            }} 
                          />
                        </Button>
                      </OverlayTrigger>
                      {onBackToGames && (
                        <OverlayTrigger
                          placement="bottom"
                          overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
                        >
                          <Button
                            onClick={onBackToGames}
                            variant="secondary"
                            size="lg"
                            style={{
                              minWidth: '48px',
                              minHeight: '48px',
                              borderRadius: '50%',
                              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                            }}
                          >
                            <LuGamepad2 size={24} />
                          </Button>
                        </OverlayTrigger>
                      )}
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="new-game-tooltip">Neues Spiel</Tooltip>}
                      >
                        <Button
                          onClick={initializeGame}
                          variant="success"
                          size="lg"
                          style={{
                            minWidth: '48px',
                            minHeight: '48px',
                            borderRadius: '50%',
                            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                          }}
                        >
                          <Icon name="refresh" size="lg" />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="mute-tooltip">{isMuted ? 'Ton einschalten' : 'Ton ausschalten'}</Tooltip>}
                      >
                        <Button
                          onClick={() => setIsMuted(!isMuted)}
                          variant={isMuted ? "outline-warning" : "warning"}
                          size="lg"
                          style={{
                            minWidth: '48px',
                            minHeight: '48px',
                            borderRadius: '50%',
                            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                          }}
                        >
                          {isMuted ? <IoVolumeMuteOutline size={24} /> : <Icon name="volume-on" size="lg" />}
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="laut-change-tooltip-mobile">Laute ändern</Tooltip>}
                      >
                        <Button
                          onClick={() => setShowLautModal(true)}
                          variant="outline-secondary"
                          size="lg"
                          style={{
                            minWidth: '60px',
                            minHeight: '60px',
                            borderRadius: '50%',
                            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                          }}
                        >
                          <Icon name="settings" size="lg" />
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </div>
                ) : (
                  // Desktop layout: all in one row
                  <div className="d-flex justify-content-center gap-3 flex-wrap">
                    {onBackToGames && (
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
                      >
                        <Button
                          onClick={onBackToGames}
                          variant="secondary"
                          size="lg"
                          style={{
                            minWidth: '60px',
                            minHeight: '60px',
                            borderRadius: '50%',
                            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                          }}
                        >
                          <LuGamepad2 size={24} />
                        </Button>
                      </OverlayTrigger>
                    )}
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="lauts-tooltip">Ausgewählte Laute</Tooltip>}
                    >
                      <div
                        style={{
                          minWidth: '80px',
                          height: '60px',
                          borderRadius: '30px',
                          backgroundColor: '#f8f9fa',
                          border: '2px solid #dee2e6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'default',
                          boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                          padding: '0 15px'
                        }}
                      >
                        {selectedLauts.join(', ')}
                      </div>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="help-tooltip">Hilfe</Tooltip>}
                    >
                      <Button
                        onClick={showHelp}
                        variant="info"
                        size="lg"
                        style={{
                          minWidth: '60px',
                          minHeight: '60px',
                          borderRadius: '50%',
                          boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                          padding: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        disabled={gameComplete || cards.length === 0}
                      >
                        <img 
                          src={helpLogo} 
                          alt="Hilfe" 
                          style={{ 
                            width: '40px', 
                            height: '40px',
                            objectFit: 'contain'
                          }} 
                        />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="new-game-tooltip">Neues Spiel</Tooltip>}
                    >
                      <Button
                        onClick={initializeGame}
                        variant="success"
                        size="lg"
                        style={{
                          minWidth: '60px',
                          minHeight: '60px',
                          borderRadius: '50%',
                          boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        <Icon name="refresh" size="lg" />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="mute-tooltip">{isMuted ? 'Ton einschalten' : 'Ton ausschalten'}</Tooltip>}
                    >
                      <Button
                        onClick={() => setIsMuted(!isMuted)}
                        variant={isMuted ? "outline-warning" : "warning"}
                        size="lg"
                        style={{
                          minWidth: '60px',
                          minHeight: '60px',
                          borderRadius: '50%',
                          boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        {isMuted ? <IoVolumeMuteOutline size={24} /> : <Icon name="volume-on" size="lg" />}
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="laut-change-tooltip">Laute ändern</Tooltip>}
                    >
                      <Button
                        onClick={() => setShowLautModal(true)}
                        variant="outline-secondary"
                        size="lg"
                        style={{
                          minWidth: '60px',
                          minHeight: '60px',
                          borderRadius: '50%',
                          boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        <Icon name="settings" size="lg" />
                      </Button>
                    </OverlayTrigger>
                  </div>
                )}
              </div>
            </>
          )}

        </>
      ) : (
        <div className="text-center">
          <p>Wähle zuerst die Laute aus, die geübt werden sollen.</p>
        </div>
      )}

      {/* Laut Selection Modal */}
      <Modal
        show={showLautModal}
        onHide={onBackToGames || (() => setShowLautModal(false))}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h4 style={{ margin: 0 }}>Laute auswählen</h4>
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="laut-info-tooltip">Wählen Sie bis zu 4 Laute aus, um Wörter mit diesen Lauten für das Zwillingspaare finden-Spiel zu verwenden.</Tooltip>}
              >
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  backgroundColor: '#17a2b8', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  <Icon name="info" size="sm" />
                </div>
              </OverlayTrigger>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: '2rem' }}>
            <p className="text-center mb-4" style={{ 
              fontSize: '16px',
              color: '#7A7A7A',
              marginBottom: '1.5rem'
            }}>
              Wähle den Spielmodus:
            </p>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
              marginBottom: '2rem'
            }}>
              <Button
                onClick={() => selectGameMode('single')}
                style={{
                  minWidth: '150px',
                  minHeight: '60px',
                  borderRadius: '15px',
                  backgroundColor: gameMode === 'single' ? '#88D8B0' : '#f8f9fa',
                  border: '2px solid',
                  borderColor: gameMode === 'single' ? '#88D8B0' : '#dee2e6',
                  color: gameMode === 'single' ? '#4A4A4A' : '#7A7A7A',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  boxShadow: gameMode === 'single' 
                    ? '0 4px 12px rgba(136, 216, 176, 0.3)' 
                    : '0 2px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                Einzelspieler
              </Button>
              <Button
                onClick={() => selectGameMode('two-player')}
                style={{
                  minWidth: '150px',
                  minHeight: '60px',
                  borderRadius: '15px',
                  backgroundColor: gameMode === 'two-player' ? '#FF6B6B' : '#f8f9fa',
                  border: '2px solid',
                  borderColor: gameMode === 'two-player' ? '#FF6B6B' : '#dee2e6',
                  color: gameMode === 'two-player' ? 'white' : '#7A7A7A',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  boxShadow: gameMode === 'two-player' 
                    ? '0 4px 12px rgba(255, 107, 107, 0.3)' 
                    : '0 2px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                Zwei Spieler
              </Button>
            </div>
          </div>
          <div style={{ 
            borderTop: '1px solid #dee2e6',
            paddingTop: '2rem'
          }}>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Wählen Sie bis zu 4 Laute aus ({selectedLauts.length}/4 ausgewählt):
            </p>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
              gap: '15px',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '10px'
            }}>
              {availableLauts.map(laut => {
                const isSelected = selectedLauts.includes(laut);
                const isDisabled = !isSelected && selectedLauts.length >= 4;
                
                return (
                  <button
                    key={laut}
                    onClick={() => handleLautSelection(laut)}
                    disabled={isDisabled}
                    style={{
                      padding: '15px 10px',
                      border: isSelected ? '3px solid #28a745' : '2px solid #dee2e6',
                      borderRadius: '10px',
                      backgroundColor: isSelected ? '#d4edda' : isDisabled ? '#f8f9fa' : '#ffffff',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: isSelected ? '#155724' : isDisabled ? '#6c757d' : '#333',
                      transition: 'all 0.3s ease',
                      minHeight: '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: isDisabled ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isDisabled) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDisabled) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {laut}
                  </button>
                );
              })}
            </div>
            {selectedLauts.length >= 4 && (
              <p style={{ marginTop: '15px', color: '#dc3545', fontSize: '14px' }}>
                Maximale Anzahl von Lauten erreicht. Entfernen Sie einen Laut, um einen anderen auszuwählen.
              </p>
            )}
            <div className="text-center" style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: '1rem',
              border: '2px solid #dee2e6',
              marginTop: '20px'
            }}>
              <div>
                <p className="mb-2" style={{ 
                  fontSize: '16px',
                  color: '#4A4A4A',
                  fontWeight: 'bold'
                }}>
                  Spielmodus: 
                  <span style={{ 
                    color: gameMode === 'single' ? '#88D8B0' : '#FF6B6B',
                    marginLeft: '8px'
                  }}>
                    {gameMode === 'single' ? 'Einzelspieler' : 'Zwei Spieler'}
                  </span>
                </p>
                <p className="mb-2" style={{ 
                  fontSize: '16px',
                  color: '#4A4A4A',
                  fontWeight: 'bold'
                }}>
                  {selectedLauts.length} Laute ausgewählt: 
                  <span style={{ 
                    color: '#88D8B0',
                    marginLeft: '8px'
                  }}>
                    {selectedLauts.join(', ')}
                  </span>
                </p>
                {selectedLauts.length > 0 && (
                  <p className="mb-0" style={{ 
                    fontSize: '14px',
                    color: hasEnoughWords() ? '#28a745' : '#dc3545',
                    fontWeight: 'bold'
                  }}>
                    Verfügbare Wörter: {getAvailableWordsCount()} / {getMinimumWordsNeeded()} benötigt
                    {!hasEnoughWords() && (
                      <>
                        <br />
                        <small style={{ color: '#dc3545' }}>
                          ⚠️ Mehr Laute erforderlich für {difficulty === 'easy' ? 'Einfach' : difficulty === 'medium' ? 'Mittel' : 'Schwer'} Modus
                        </small>
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={resetLautSelection}
            disabled={selectedLauts.length === 0}
          >
            Zurücksetzen
          </Button>
          <Button 
            variant="success" 
            onClick={startGameWithLauts}
            disabled={selectedLauts.length === 0 || !hasEnoughWords()}
            style={{
              minWidth: '200px',
              backgroundColor: (selectedLauts.length > 0 && hasEnoughWords()) ? '#28a745' : '#6c757d',
              borderColor: (selectedLauts.length > 0 && hasEnoughWords()) ? '#28a745' : '#6c757d',
              opacity: (selectedLauts.length === 0 || !hasEnoughWords()) ? 0.6 : 1
            }}
          >
            {selectedLauts.length === 0 
              ? 'Laute auswählen' 
              : !hasEnoughWords() 
                ? `Mehr Laute benötigt (${getAvailableWordsCount()}/${getMinimumWordsNeeded()})`
                : `Spiel starten (${selectedLauts.length} Laut${selectedLauts.length !== 1 ? 'e' : ''})`
            }
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Word Prompt Modal */}
      <Modal 
        show={showWordPrompt} 
        onHide={handleWordPromptClose}
        backdrop="static"
        keyboard={false}
        size="lg" 
        centered
      >
        <Modal.Body style={{ 
          padding: '3rem',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '0.375rem'
        }}>
          <div style={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: '#28a745',
            marginBottom: '20px'
          }}>
            ✓
          </div>
          <h3 style={{ 
            color: '#4A4A4A',
            marginBottom: '20px',
            fontWeight: 'bold'
          }}>
            Gut gemacht!
          </h3>
          <div style={{
            fontSize: '36px',
            fontWeight: 'bold',
            color: '#FF6B6B',
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '15px',
            border: '3px solid #FF6B6B'
          }}>
            {matchedWord}
          </div>
          <p style={{ 
            fontSize: '20px',
            color: '#7A7A7A',
            marginBottom: '20px'
          }}>
            Versuche auch das Wort zu sprechen
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            marginBottom: '20px'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: '#FF6B6B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white'
            }}>
              1
            </div>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: '#FF6B6B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white'
            }}>
              2
            </div>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: '#FF6B6B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white'
            }}>
              3
            </div>
          </div>
          <Button 
            onClick={handleWordPromptClose}
            style={{
              minWidth: '150px',
              minHeight: '50px',
              borderRadius: '25px',
              backgroundColor: '#28a745',
              border: '2px solid #28a745',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Weiter
          </Button>
        </Modal.Body>
      </Modal>

      {/* Confetti Effect */}
      {gameComplete && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={showConfetti}
          numberOfPieces={200}
          gravity={0.3}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
        />
      )}
        </>
    </Container>
    <FloatingLautbilderButton selectedLauts={selectedLauts} />
    </div>
  );
}

export default MemoryGame;